/*============================================================
LOGIC VALIDATOR
============================================================*/
export async function validateLogic(params: any) {
  const { cart, logicMethod } = params;

  const storeConfig = JSON.parse(localStorage.getItem('storeConfiguration') || '{}');
  const logic = storeConfig.data.logic || {};
  
  switch (logicMethod) {
    /*SHIPPING
    =======================*/
    case 'shipping':
      const shippingLogicResult = await checkShippingLogics(logic.shipping_logic, cart);
      return {
        shippingLogic: shippingLogicResult
      };
      
    /*PURCHASE ORDER
    =======================*/
    case 'purchase_order':
      const po = await checkPurchaseOrderLogic(logic.purchase_order, cart);
      return {
        purchase_order: po
      };
      
    default:
      return {
        status: "noLogics"
      };
  }
}

/*============================================================
CHECK SHIPPING LOGICS
============================================================*/
async function checkShippingLogics(shippingLogicArr: any[], cart: any) {
  let result: any[] = [];
  
  if (!shippingLogicArr || !Array.isArray(shippingLogicArr)) {
    return result;
  }
  
  for (const logicItem of shippingLogicArr) {
    // Check if all rules are satisfied for this logic
    const rulesResult = await checkLogicRules(logicItem.rules, cart.products);
    
    if (rulesResult) {
      // Keep the existing output format
      if (logicItem.addToAll) {
        result.push({
          addToAll: true,
          price: logicItem.price || 0, // Default to 0 if price is not defined
          shippingMethods: []
        });
      } else {
        result.push({
          addToAll: false,
          shippingMethods: logicItem.shippingMethods || []
        });
      }
    }
  }
  
  return result;
}

/*============================================================
CHECK PURCHASE ORDER LOGIC
============================================================*/
async function checkPurchaseOrderLogic(poLogic: any, cart: any) {
  if (!poLogic || !Array.isArray(poLogic) || poLogic.length === 0) {
    return false; 
  }
  
  // Iterate through each logic item in the array
  for (const logicItem of poLogic) {
    if (!logicItem.rules || !Array.isArray(logicItem.rules)) {
      continue; // Skip this logic item and check the next one
    }
    
    const rulesResult = await checkLogicRules(logicItem.rules, cart.products);
    
    if (rulesResult) {
      return true; // Return true if any logic item's rules are satisfied
    }
  }
  
  return false; // Return false if no logic item's rules are satisfied
}

/*============================================================
CHECK LOGIC RULES
============================================================*/
async function checkLogicRules(rules: any[], products: any[]) {
  if (!rules || !Array.isArray(rules)) {
    return true; // If no rules, return true
  }
  
  for (const rule of rules) {
    const { value, rule: ruleName, custom_value = "", condition: ruleCondition } = rule;
    
    let orderVariable;
    
    switch (ruleName) {
      case 'cart_total':
        orderVariable = products.reduce((acc, product) => acc + (product.product_price * (product.quantity || product.product_quantity || 1)), 0);
        break;
        
      case 'weight':
        orderVariable = products.reduce((acc, product) => acc + ((product.weight || 0) * (product.quantity || product.product_quantity || 1)), 0);
        break;
        
      case 'category':
        const matchingProducts = products.filter(product => 
          product.category && product.category.toLowerCase() === value.toLowerCase());
          
        const categoryMatch = matchingProducts.length > 0;
        if (categoryMatch) {
          if (ruleCondition === '==') {
            // Case sensitive check
            const exactMatchingProducts = products.filter(product => 
              product.category === value);
              
            const exactCategoryMatch = exactMatchingProducts.length > 0;
            if (exactCategoryMatch) {
              continue; // Pass this rule
            } else {
              return false; // Fail this rule
            }
          }
          continue; // Pass this rule
        } else {
          return false; // Fail this rule
        }
        break;
        
      case 'custom':
        if (!isNaN(Number(value))) {
          // Numeric custom value
          const product = products.find(product => {
            const customInputs = product.custom_inputs || [];
            return customInputs.some((attribute:any) => 
              attribute.name && attribute.name.toLowerCase() === custom_value.toLowerCase());
          });
          
          if (product) {
            const customAttribute = (product.custom_inputs || []).find((attribute: any) => 
              attribute.name && attribute.name.toLowerCase() === custom_value.toLowerCase());
            orderVariable = Number(customAttribute.value);
          } else {
            return false; // No match found
          }
        } else {
          // String custom value
          const matchingProducts = products.filter(product => {
            const customInputs = product.custom_inputs || [];
            return customInputs.some((attribute:any) => 
              attribute.name && 
              attribute.name.toLowerCase() === custom_value.toLowerCase() && 
              attribute.value && 
              attribute.value.toLowerCase() === value.toLowerCase());
          });
          
          const customMatch = matchingProducts.length > 0;
          if (customMatch) {
            if (ruleCondition === '==') {
              // Case sensitive check
              const exactMatchingProducts = products.filter(product => {
                const customInputs = product.custom_inputs || [];
                return customInputs.some((attribute: any) => 
                  attribute.name === custom_value && 
                  attribute.value === value);
              });
              
              const exactCustomMatch = exactMatchingProducts.length > 0;
              if (exactCustomMatch) {
                continue; // Pass this rule
              } else {
                return false; // Fail this rule
              }
            }
            continue; // Pass this rule
          } else if (ruleCondition === '!=') {
            // Not equals check
            const notEqualsMatchingProducts = products.filter(product => {
              const customInputs = product.custom_inputs || [];
              return customInputs.some((attribute: any) => 
                attribute.name === custom_value && 
                attribute.value !== value);
            });
            
            const notEqualsMatch = notEqualsMatchingProducts.length > 0;
            if (notEqualsMatch) {
              continue; // Pass this rule
            } else {
              return false; // Fail this rule
            }
          } else {
            return false; // No match found
          }
        }
        break;
        
      default:
        return false;
    }
    
    // Evaluate numeric rules with their conditions
    switch (ruleCondition) {
      case '=':
      case '==':
        if (orderVariable !== Number(value)) {
          return false;
        }
        break;
        
      case '>':
        if (orderVariable <= Number(value)) {
          return false;
        }
        break;
        
      case '<':
        if (orderVariable >= Number(value)) {
          return false;
        }
        break;
        
      case '>=':
        if (orderVariable < Number(value)) {
          return false;
        }
        break;
        
      case '<=':
        if (orderVariable > Number(value)) {
          return false;
        }
        break;
        
      case '!=':
        if (orderVariable === Number(value)) {
          return false;
        }
        break;
    }
  }
  
  return true; // All rules passed
}
